// src/components/analytics/AILearningDashboard.js
// --- UPDATE (round 3: live + real) ----------------------------------------------------------------
// • Model status strip: every trainable model with its production version, metric, new feedback
//   since the last training, a challenger waiting for approval and a run in progress — live from the
//   ML control plane (useMlOverview refreshes on 'ml_events').
// • Live refresh: when a model is trained / promoted or feedback is recorded (liveVersion +
//   lastLiveEvent from the Analytics Hub), the learning widgets reload — at most every 30 s, and
//   not while a widget is being used for training (the training widgets keep their own state).
// • Export works: JSON (models, recent runs and audit events) or CSV (one row per model). It was a
//   toast that downloaded nothing.
// • New "Model Monitor" tab (AIMetricsDashboard). The RUL widget is no longer mounted twice.
// • Ask AI / Explain on each model.
// --- END UPDATE ------------------------------------------------------------------------------------
import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Heading, SimpleGrid, useColorModeValue, Tabs, TabList, TabPanels, Tab, TabPanel, Badge, HStack,
  Button, Icon, useToast, Collapse, IconButton, Tooltip, Text, VStack, Skeleton, Menu, MenuButton, MenuList,
  MenuItem, Progress, Alert, AlertIcon, Flex,
} from '@chakra-ui/react';
import { FaRobot, FaBrain, FaChartLine, FaDownload, FaSync, FaChevronDown, FaChevronUp, FaHeartbeat } from 'react-icons/fa';
import { MdAutoAwesome } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import AiDispatchFeedbackWidget from './AiDispatchFeedbackWidget';
import RulModelInsightsWidget from './RulModelInsightsWidget';
import DispatcherTrainingWidget from './DispatcherTrainingWidget';
import NlpPartPredictorWidget from './NlpPartPredictorWidget';
import HistoricalFailureUploader from './HistoricalFailureUploader';
import AISuggestedRulesWidget from './AISuggestedRulesWidget';
import IssueClassifierTrainingWidget from './IssueClassifierTrainingWidget';
import AIMetricsDashboard from './AIMetricsDashboard';
import { useMlOverview } from '../../hooks/useMlOps';
import { ExplainButton } from '../ml/FlowSenseAIExplainer';
import { fmtMetric, timeAgo } from '../ml/mlFormat';

const LIVE_TYPES = new Set(['ml_events', 'ai_model_promoted', 'ai_feedback_recorded', 'nlp_feedback_processed', 'rul_batch_complete']);
const LIVE_MIN_GAP_MS = 30000;

const downloadBlob = (content, type, filename) => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
const csvCell = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function ModelStatusStrip({ ov, loading, error, onRefresh, onAskAI, onOpen }) {
  const cardBg = useColorModeValue('white', 'navy.700');
  const border = useColorModeValue('gray.200', 'navy.600');
  const muted = useColorModeValue('gray.500', 'gray.400');
  if (error && !ov) {
    return <Alert status="warning" borderRadius="md" mb={4} fontSize="sm"><AlertIcon />Model status unavailable: {error.message}<Button ml="auto" size="xs" onClick={onRefresh}>Retry</Button></Alert>;
  }
  return (
    <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={3} mb={5} data-testid="model-status-strip">
      {(!ov && loading ? [0, 1, 2, 3] : ov?.models || []).map((m) => (typeof m === 'number' ? <Skeleton key={m} h="110px" borderRadius="lg" /> : (
        <Box key={m.key} bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="lg" p={3} data-testid={`model-card-${m.key}`}>
          <Flex justify="space-between" align="flex-start" mb={1}>
            <Text fontWeight="700" fontSize="sm" noOfLines={1}>{m.name}</Text>
            <ExplainButton topic={m.key} label="" aria-label={`Explain ${m.name}`} />
          </Flex>
          <HStack spacing={1} mb={2} flexWrap="wrap">
            {m.activeJob ? <Badge colorScheme="blue">training {m.activeJob.progress ? `${m.activeJob.progress}%` : ''}</Badge>
              : m.champion ? <Badge colorScheme="green">v{m.champion.model_version}</Badge>
                : <Badge colorScheme="orange">no production version</Badge>}
            {m.challenger?.verdict?.decision === 'promote' && <Badge colorScheme="purple">challenger waiting</Badge>}
            {m.lastJob?.status === 'failed' && <Badge colorScheme="red">last run failed</Badge>}
          </HStack>
          {m.activeJob && <Progress size="xs" value={m.activeJob.progress || 5} isIndeterminate={!m.activeJob.progress} colorScheme="blue" mb={2} borderRadius="full" />}
          <Text fontSize="xs" color={muted}>
            {m.champion ? `${m.metric?.label}: ${fmtMetric(m.champion.metric, m.metric)} · trained ${timeAgo(m.champion.last_trained_at || m.champion.created_at)}` : (m.data?.records !== undefined ? `${m.data.records} training records (needs ${m.data.minRecords})` : 'No data count')}
          </Text>
          {m.data?.newSinceTraining !== null && m.data?.newSinceTraining !== undefined && (
            <Text fontSize="xs" color={muted}>{m.data.newSinceTraining} new records since last training</Text>
          )}
          <HStack mt={2} spacing={2}>
            <Button size="xs" variant="link" colorScheme="purple" onClick={() => onOpen(m.key)}>Open</Button>
            {onAskAI && <Button size="xs" variant="link" colorScheme="purple" onClick={() => onAskAI(`Should ${m.name} be retrained now? Explain using its current metric and new feedback.`)}>Ask AI</Button>}
          </HStack>
        </Box>
      )))}
    </SimpleGrid>
  );
}

const AILearningDashboard = ({ dateRange, liveVersion = 0, lastLiveEvent = null, onAskAI = null }) => {
  const cardBg = useColorModeValue('white', 'navy.700');
  const borderColor = useColorModeValue('gray.200', 'navy.600');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const [activeTab, setActiveTab] = useState(0);
  const [isExpanded, setIsExpanded] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastLiveRefresh, setLastLiveRefresh] = useState(null);
  const toast = useToast();
  const navigate = useNavigate();
  const { data: ov, loading, error, refresh } = useMlOverview();

  const lastLive = useRef(liveVersion);
  const lastBump = useRef(0);
  const liveTimer = useRef(null);
  useEffect(() => {
    if (liveVersion === lastLive.current) return undefined;
    lastLive.current = liveVersion;
    if (!lastLiveEvent || !LIVE_TYPES.has(lastLiveEvent.type)) return undefined;
    const p = lastLiveEvent.payload || {};
    // job progress ticks change nothing the learning widgets show
    if (lastLiveEvent.type === 'ml_events' && !['job_finished', 'model_promoted', 'model_rolled_back', 'model_registered', 'model_archived', 'model_deleted'].includes(p.type || p.event)) return undefined;
    if (liveTimer.current) return undefined;
    const wait = Math.max(LIVE_MIN_GAP_MS - (Date.now() - lastBump.current), 0);
    liveTimer.current = setTimeout(() => {
      liveTimer.current = null;
      lastBump.current = Date.now();
      setRefreshKey(k => k + 1);
      setLastLiveRefresh(new Date());
    }, wait);
    return undefined;
  }, [liveVersion, lastLiveEvent]);
  useEffect(() => () => { if (liveTimer.current) clearTimeout(liveTimer.current); }, []);

  const handleRefreshAll = useCallback(() => {
    setRefreshKey(prev => prev + 1);
    refresh();
    toast({ title: 'Refreshing AI insights', status: 'info', duration: 1500, isClosable: true });
  }, [toast, refresh]);

  const exportJson = useCallback(() => {
    if (!ov) { toast({ title: 'Nothing to export yet', status: 'warning', duration: 2500 }); return; }
    const report = {
      generatedAt: new Date().toISOString(),
      period: dateRange ? { start: dateRange.start, end: dateRange.end } : null,
      summary: ov.summary,
      models: ov.models.map(m => ({
        key: m.key, name: m.name, metric: m.metric?.label,
        production: m.champion ? { version: m.champion.model_version, metric: m.champion.metric, trainedAt: m.champion.last_trained_at || m.champion.created_at } : null,
        challenger: m.challenger ? { version: m.challenger.model_version, metric: m.challenger.metric, decision: m.challenger.verdict?.decision } : null,
        records: m.data?.records ?? null, newSinceTraining: m.data?.newSinceTraining ?? null,
        lastRun: m.lastJob ? { status: m.lastJob.status, decision: m.lastJob.decision, at: m.lastJob.finished_at || m.lastJob.created_at } : null,
      })),
      recentJobs: ov.recentJobs, recentEvents: ov.recentEvents, attention: ov.attention,
    };
    downloadBlob(JSON.stringify(report, null, 2), 'application/json', `flowsense-ai-learning-${new Date().toISOString().slice(0, 10)}.json`);
    toast({ title: 'AI learning report downloaded', status: 'success', duration: 2000 });
  }, [ov, dateRange, toast]);

  const exportCsv = useCallback(() => {
    if (!ov) { toast({ title: 'Nothing to export yet', status: 'warning', duration: 2500 }); return; }
    const head = ['model', 'production_version', 'metric', 'value', 'trained_at', 'challenger_version', 'challenger_decision', 'records', 'new_since_training', 'last_run_status'];
    const rows = ov.models.map(m => [m.name, m.champion?.model_version, m.metric?.label, m.champion?.metric, m.champion?.last_trained_at || m.champion?.created_at, m.challenger?.model_version, m.challenger?.verdict?.decision, m.data?.records, m.data?.newSinceTraining, m.lastJob?.status]);
    downloadBlob([head, ...rows].map(r => r.map(csvCell).join(',')).join('\n'), 'text/csv', `flowsense-ai-models-${new Date().toISOString().slice(0, 10)}.csv`);
    toast({ title: 'Model summary downloaded', status: 'success', duration: 2000 });
  }, [ov, toast]);

  const openModel = useCallback((key) => navigate(`/admin/ai/grading?tab=registry&model=${encodeURIComponent(key)}`), [navigate]);

  const tabCategories = [
    { label: 'Overview', icon: FaChartLine, badge: null },
    { label: 'Predictive Models', icon: FaBrain, badge: 'ML' },
    { label: 'Learning & Training', icon: FaRobot, badge: ov?.summary?.activeJobs ? `${ov.summary.activeJobs} running` : null },
    { label: 'Model Monitor', icon: FaHeartbeat, badge: ov && !ov.summary?.mlOnline ? 'offline' : null },
  ];
  const card = { bg: cardBg, p: 5, borderRadius: 'lg', boxShadow: 'md', borderWidth: '1px', borderColor, transition: 'all 0.3s', _hover: { boxShadow: 'lg', transform: 'translateY(-2px)' } };

  return (
    <Box>
      <HStack justify="space-between" mb={4} wrap="wrap" spacing={4}>
        <HStack spacing={4}>
          <VStack align="start" spacing={0}>
            <Heading size="lg">AI & Learning Insights</Heading>
            <Text fontSize="xs" color={muted}>{lastLiveRefresh ? `Live-updated ${timeAgo(lastLiveRefresh)}` : 'Updates live when models are trained or feedback arrives'}</Text>
          </VStack>
          <IconButton icon={isExpanded ? <FaChevronUp /> : <FaChevronDown />} onClick={() => setIsExpanded(!isExpanded)} size="sm" variant="ghost" aria-label="Toggle expand" />
        </HStack>
        <HStack spacing={2}>
          {onAskAI && <Button size="sm" variant="outline" colorScheme="purple" leftIcon={<MdAutoAwesome />} onClick={() => onAskAI('Which FlowSense AI model would benefit most from retraining now, and why?')}>Ask AI</Button>}
          <Tooltip label="Refresh all AI insights">
            <Button leftIcon={<Icon as={FaSync} />} size="sm" onClick={handleRefreshAll} variant="outline">Refresh</Button>
          </Tooltip>
          <Menu placement="bottom-end">
            <MenuButton as={Button} leftIcon={<Icon as={FaDownload} />} size="sm" colorScheme="blue" isDisabled={!ov}>Export</MenuButton>
            <MenuList fontSize="sm">
              <MenuItem onClick={exportJson}>Full report (JSON)</MenuItem>
              <MenuItem onClick={exportCsv}>Model summary (CSV)</MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </HStack>

      <ModelStatusStrip ov={ov} loading={loading} error={error} onRefresh={refresh} onAskAI={onAskAI} onOpen={openModel} />

      <Collapse in={isExpanded} animateOpacity>
        <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed" colorScheme="blue" isLazy>
          <TabList>
            {tabCategories.map((tab) => (
              <Tab key={tab.label}>
                <HStack spacing={2}>
                  <Icon as={tab.icon} />
                  <span>{tab.label}</span>
                  {tab.badge && <Badge colorScheme={tab.badge === 'offline' ? 'red' : 'green'} fontSize="xs">{tab.badge}</Badge>}
                </HStack>
              </Tab>
            ))}
          </TabList>

          <TabPanels>
            <TabPanel>
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                <Box {...card}><HistoricalFailureUploader dateRange={dateRange} key={`uploader-${refreshKey}`} /></Box>
                <Box {...card}><AISuggestedRulesWidget key={`rules-${refreshKey}`} /></Box>
              </SimpleGrid>
            </TabPanel>
            <TabPanel>
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                <Box {...card}><RulModelInsightsWidget dateRange={dateRange} key={`rul-pred-${refreshKey}`} /></Box>
                <Box {...card}><NlpPartPredictorWidget dateRange={dateRange} key={`nlp-${refreshKey}`} /></Box>
              </SimpleGrid>
            </TabPanel>
            <TabPanel>
              {/* training widgets keep their state across live refreshes (no refreshKey) */}
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                <Box {...card}><DispatcherTrainingWidget /></Box>
                <Box {...card}><AiDispatchFeedbackWidget dateRange={dateRange} key={`feedback-${refreshKey}`} /></Box>
                <Box {...card} gridColumn={{ base: '1', lg: '1 / -1' }}><IssueClassifierTrainingWidget /></Box>
              </SimpleGrid>
            </TabPanel>
            <TabPanel>
              <AIMetricsDashboard liveVersion={liveVersion} onAskAI={onAskAI} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Collapse>
    </Box>
  );
};

export default AILearningDashboard;
